import React, { useEffect, useState } from 'react';
import { Button, Input, notification } from 'antd';
import { ReactComponent as SaveIcon } from 'assets/svg/save.svg';
// import { ReactComponent as EyeIcon } from 'assets/svg/eye.svg';
import { ReactComponent as SendIcon } from 'assets/svg/send.svg';
import { ReactComponent as RejectIcon } from 'assets/svg/reject.svg';
import { ReactComponent as ApproveIcon } from 'assets/svg/approved.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TextField from 'views/common/TextField';
import API from 'api';
import Loading from 'views/common/Loading';
import StatusHeader from 'views/common/StatusHeader';
import { PROJECT_STATUS } from 'constants/constants';
import PopupRunTest from './PopupRunTest';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from 'store/reducers/modal';
import { MODAL_NAME } from 'constants/modal';
import PopupConfirm from 'views/components/PopupConfirm';
import History from './History';
import { checkHasAnyPermission, PERMISSION } from 'constants/permission';
import AccessControl from 'views/common/AccessControl';
import Section from 'views/common/Section';
import Share from './Share';
import { useValidate, ValidateContext } from 'views/context/ValidateContext';
import { SnippetsOutlined } from '@ant-design/icons';
import PopupIntegration from './PopupIntegration';
import GeneralInformation from './GeneralInformation';
import IDECode from './IDECode';
import { THEME } from 'utils/editorTheme';
import InputDescription from './InputDescription';
import PopupViewHistoryCode from './PopupViewHistoryCode';

const defaultaFunc = `function execute(input) {
    // TODO
}
`;

const DEFAULT = {
  inputTest: '{}',
  inputDesc: {},
};

const CreateProject = () => {
  const validate = useValidate('create-project');
  const { permissions } = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  const isCreatePage = pathname.includes('create');
  const isPageApprove = pathname.includes('check-project');
  const [stateInfo, setStateInfo] = useState(DEFAULT);
  const [codeData, setCodeData] = useState(defaultaFunc);
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [reject, setReject] = useState({});
  const isRecordWaitApprove =
    stateInfo.status === PROJECT_STATUS.WAITING_APPROVE;
  const [fileAvatar, setFileAvatar] = useState();
  const [theme, setTheme] = useState(THEME.VSCODE_THEME);

  const onChangeTheme = (theme) => {
    setTheme(theme);
    localStorage.setItem('theme-code', theme);
  };

  const onChangeRejectReason = (e) => {
    setReject({ ...reject, reason: e.target.value });
  };

  const uploadAvatar = async () => {
    const res = await API.uploadFile('user', fileAvatar);
    if (res.status === 200) {
      setFileAvatar();
      setStateInfo({ ...stateInfo, avatar: res.data.name });
      return res.data.name;
    }
    notification.error({
      message: 'Project',
      description: 'Upload avatar error',
    });
  };

  const onSave = async () => {
    const err = validate.validateFields();
    if (err) {
      notification.error({
        message: 'Project',
        description: Object.values(err).join('. '),
      });
      return;
    }

    const params = { ...stateInfo };
    if (fileAvatar) {
      const avatar = await uploadAvatar();
      if (!avatar) return;
      params.avatar = avatar;
    }

    let res = {};
    if (isCreatePage) {
      res = await API.createNewProject({
        ...params,
        content: codeData,
      });
    } else {
      res = await API.updateProject({
        ...params,
        content: codeData,
      });
    }

    if (res.status < 400) {
      navigate('/project');
      return notification.success({
        message: 'Project',
        description: 'Save success',
      });
    }

    notification.error({
      message: 'Project',
      description: 'Save error: ' + res.message,
    });
  };

  const onReject = async () => {
    if (!reject.reason) {
      return setReject({ ...reject, error: 'Reason is required' });
    }
    const res = await API.rejectProject({
      id: projectId,
      reason: reject.reason,
    });

    if (res.status === 200) {
      navigate('/check-project');
      return notification.success({
        title: 'Project',
        description: 'Reject project ' + projectId + ' success.',
      });
    }

    notification.error({
      title: 'Project',
      description: 'Reject project error: ' + res.message,
    });
  };

  const onApprove = async () => {
    const res = await API.approveProject(projectId);

    if (res.status === 200) {
      navigate('/check-project');
      return notification.success({
        title: 'Project',
        description: 'Approve project ' + projectId + ' success.',
      });
    }

    notification.error({
      title: 'Project',
      description: 'Approve project error: ' + res.message,
    });
  };

  const onSendApprove = async () => {
    const err = validate.validateFields();
    if (err) {
      notification.error({
        message: 'Project',
        description: Object.values(err).join('. '),
      });
      return;
    }

    const params = { ...stateInfo };
    if (fileAvatar) {
      const avatar = await uploadAvatar();
      if (!avatar) return;
      params.avatar = avatar;
    }

    const res = await API.sendApprove({
      ...params,
      content: codeData,
    });

    if (res.status < 400) {
      navigate('/project');
      return notification.success({
        message: 'Project',
        description: 'Send approve success',
      });
    }

    notification.error({
      message: 'Project',
      description: 'Send approve error: ' + res.message,
    });
  };

  const onPause = async () => {
    if (!reject.reason) {
      return setReject({ ...reject, error: 'Reason is required' });
    }
    const res = await API.pauseDCS({
      dcsId: projectId,
      reason: reject.reason,
    });

    if (res.status === 200) {
      navigate('/check-project');
      return notification.success({
        title: 'Project',
        description: 'Pause project ' + projectId + ' success.',
      });
    }

    notification.error({
      title: 'Project',
      description: 'Pause project error: ' + res.message,
    });
  };

  const onResume = async () => {
    if (!reject.reason) {
      return setReject({ ...reject, error: 'Reason is required' });
    }
    const res = await API.resumeDCS({
      dcsId: projectId,
      reason: reject.reason,
    });

    if (res.status === 200) {
      navigate('/check-project');
      return notification.success({
        title: 'Project',
        description: 'Resume project ' + projectId + ' success.',
      });
    }

    notification.error({
      title: 'Project',
      description: 'Resume project error: ' + res.message,
    });
  };

  const getDetail = async () => {
    if (!projectId || loading) return;
    setLoading(true);
    const callApi = isPageApprove ? API.getDetailReviewer : API.getDetail;
    const res = await callApi(projectId);
    setLoading(false);
    if (res.status === 200) {
      setStateInfo(res.data);
      setCodeData(res.data.content);
      return;
    }

    notification.error({
      message: 'Project',
      description: 'Get detail error: ' + res.message,
    });
  };

  // const openRun = () => {
  //   dispatch(
  //     openModal({
  //       name: MODAL_NAME.RUN_TEST,
  //       data: { inputTest: stateInfo.inputTest },
  //     })
  //   );
  // };

  const onIntegration = () => {
    dispatch(
      openModal({
        name: MODAL_NAME.INTEGRATION,
        data: { inputDesc: stateInfo.inputDesc },
      })
    );
  };

  useEffect(() => {
    getDetail();
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    if (localStorage.getItem('theme-code')) {
      setTheme(localStorage.getItem('theme-code'));
    }
  }, []);

  const permissionDisabled = !checkHasAnyPermission(permissions, [
    isCreatePage ? PERMISSION.CREATE_DCS : PERMISSION.UPDATE_DCS,
  ]);

  const pauseDisabled = stateInfo.pause;

  return (
    <ValidateContext.Provider value={validate}>
      <div className="create-project-wraper">
        {loading && <Loading />}
        <PopupRunTest isPageApprove={isPageApprove} />
        <PopupIntegration projectId={projectId} stateInfo={stateInfo} />
        <PopupViewHistoryCode />
        <div className="create-project-title">
          {isCreatePage
            ? 'Create Project'
            : isPageApprove
            ? 'Check Project'
            : 'Update Project'}
        </div>
        <div className="create-project-header">
          <StatusHeader
            options={[
              'Creating',
              'Waiting Approve',
              'Approved',
              'Rejected',
              'Paused',
            ]}
            status={
              stateInfo.pause
                ? 'Paused'
                : stateInfo.status === PROJECT_STATUS.APPROVED
                ? 'Approved'
                : stateInfo.status === PROJECT_STATUS.WAITING_APPROVE
                ? 'Waiting Approve'
                : stateInfo.status === PROJECT_STATUS.REJECT
                ? 'Rejected'
                : 'Creating'
            }
          />
        </div>
        <div className="create-project">
          <Section title="General Information" defaultOpen>
            <GeneralInformation
              stateInfo={stateInfo}
              isPageApprove={isPageApprove}
              permissionDisabled={permissionDisabled || pauseDisabled}
              onChange={setStateInfo}
              onChangeFileAvatar={setFileAvatar}
            />
          </Section>
          <Section title="IDE code" defaultOpen>
            <IDECode
              codeData={codeData}
              theme={theme}
              onChangeTheme={onChangeTheme}
              onChange={setCodeData}
              isPageApprove={isPageApprove}
              permissionDisabled={permissionDisabled || pauseDisabled}
            />
          </Section>

          <Section title="Input Description" defaultOpen>
            <InputDescription
              onChange={setStateInfo}
              stateInfo={stateInfo}
              isPageApprove={isPageApprove}
              permissionDisabled={permissionDisabled || pauseDisabled}
              theme={theme}
            />
          </Section>

          <div className="section-button">
            {!isCreatePage && !isPageApprove && (
              <Button
                onClick={onIntegration}
                className="btn-w btn-custom"
                icon={<SnippetsOutlined />}
                disabled={stateInfo.pause}
              >
                Integration
              </Button>
            )}
            <AccessControl permissions={[PERMISSION.UPDATE_DCS]}>
              {!isPageApprove &&
                (stateInfo.status === PROJECT_STATUS.WAITING_APPROVE ||
                stateInfo.status === PROJECT_STATUS.APPROVED ? (
                  <PopupConfirm
                    content="Are you sure to save this project? You will need to resubmit to admin."
                    onOk={onSave}
                  >
                    {({ onOpen }) => (
                      <Button
                        onClick={onOpen}
                        className="btn-w btn-custom"
                        icon={<SaveIcon />}
                        disabled={stateInfo.pause}
                      >
                        Save
                      </Button>
                    )}
                  </PopupConfirm>
                ) : (
                  <Button
                    onClick={onSave}
                    className="btn-w btn-custom"
                    icon={<SaveIcon />}
                    disabled={stateInfo.pause}
                  >
                    Save
                  </Button>
                ))}
            </AccessControl>
            {isPageApprove ? (
              <>
                {isRecordWaitApprove && (
                  <>
                    <AccessControl permissions={[PERMISSION.REJECT_DCS]}>
                      <PopupConfirm
                        title="Confirm"
                        onOk={onReject}
                        content="Are you sure to reject this project? Please enter a reason in the below box!"
                        expand={
                          <TextField
                            label="Reason"
                            required
                            helperText={reject.error}
                          >
                            <Input.TextArea
                              value={reject.reason}
                              onChange={onChangeRejectReason}
                              rows={5}
                              placeholder="Enter reason"
                            />
                          </TextField>
                        }
                      >
                        {({ onOpen }) => (
                          <Button
                            className="btn-custom btn-s"
                            type="primary"
                            icon={<RejectIcon />}
                            onClick={onOpen}
                            disabled={stateInfo.pause}
                          >
                            Reject
                          </Button>
                        )}
                      </PopupConfirm>
                    </AccessControl>
                    <AccessControl permissions={[PERMISSION.APPROVE_DCS]}>
                      <PopupConfirm
                        title="Confirm"
                        onOk={onApprove}
                        content="Are you sure to approve this project?"
                      >
                        {({ onOpen }) => (
                          <Button
                            className="btn-custom btn-s"
                            type="primary"
                            icon={<ApproveIcon />}
                            onClick={onOpen}
                            disabled={stateInfo.pause}
                          >
                            Approve
                          </Button>
                        )}
                      </PopupConfirm>
                    </AccessControl>
                  </>
                )}
                {stateInfo.pause ? (
                  <AccessControl permissions={[PERMISSION.RESUME_DCS]}>
                    <PopupConfirm
                      title="Confirm"
                      onOk={onResume}
                      content="Are you sure to resume this project?"
                      expand={
                        <TextField
                          label="Reason"
                          required
                          helperText={reject.error}
                        >
                          <Input.TextArea
                            value={reject.reason}
                            onChange={onChangeRejectReason}
                            rows={5}
                            placeholder="Enter reason"
                          />
                        </TextField>
                      }
                    >
                      {({ onOpen }) => (
                        <Button
                          className="btn-custom btn-s"
                          type="primary"
                          icon={<ApproveIcon />}
                          onClick={onOpen}
                        >
                          Resume
                        </Button>
                      )}
                    </PopupConfirm>
                  </AccessControl>
                ) : (
                  <AccessControl permissions={[PERMISSION.PAUSE_DCS]}>
                    <PopupConfirm
                      title="Confirm"
                      onOk={onPause}
                      content="Are you sure to pause this project?"
                      expand={
                        <TextField
                          label="Reason"
                          required
                          helperText={reject.error}
                        >
                          <Input.TextArea
                            value={reject.reason}
                            onChange={onChangeRejectReason}
                            rows={5}
                            placeholder="Enter reason"
                          />
                        </TextField>
                      }
                    >
                      {({ onOpen }) => (
                        <Button
                          className="btn-custom btn-s"
                          type="primary"
                          icon={<ApproveIcon />}
                          onClick={onOpen}
                        >
                          Pause
                        </Button>
                      )}
                    </PopupConfirm>
                  </AccessControl>
                )}
              </>
            ) : (
              <AccessControl permissions={[PERMISSION.SEND_APPROVE_DCS]}>
                <Button
                  className="btn-custom btn-w"
                  icon={<SendIcon />}
                  onClick={onSendApprove}
                  disabled={stateInfo.pause}
                >
                  Submit for review
                </Button>
              </AccessControl>
            )}
          </div>

          {!isCreatePage && !isPageApprove && stateInfo.accessLevel === 'PRIVATE' && (
            <Section title="Share">
              <Share pauseDisabled={pauseDisabled} dcsId={projectId} validate={validate} />
            </Section>
          )}

          {!isCreatePage && (
            <Section title="History">
              <History
                versionCurrent={stateInfo.versionName}
                isPageApprove={isPageApprove}
                projectId={projectId}
              />
            </Section>
          )}
        </div>
      </div>
    </ValidateContext.Provider>
  );
};

export default CreateProject;
